import {
  Flex,
  HStack,
  Spacer,
  useToast,
  Text,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';

import {
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';
import {
  getValleyBankArchiveThresoldDate,
  VALLEY_BANK_ARCHIVE_THRESHOLD_DAYS,
} from '@finley-technologies/valley-bank-utils';

import { useRestoreValleyBankReportingDateMutation } from 'types/dashboard-core-gqlTypes.generated.d';

import { DashboardMode } from '../hooks/DataMetadataContextType';
import {
  getBgColorForDashboardMode,
  getHeaderElementProps,
  HeaderProps,
} from './utils';
import { ValleyBankPreviewPicker } from './ValleyBankPreviewPicker';
import { colors } from '~/packages/common-frontend/colors';
import { ProgressAlert } from '~/packages/common-frontend/components/Alert';
import { Icon } from '~/packages/common-frontend/components/icons/fontAwesome/Icon';
import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { HeaderRow } from '~/packages/common-frontend/layout';
import { useSubheaderDomNode } from '~/packages/common-frontend/layout/Layout';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';
import { DownloadMenuButton } from '~/valleyBank/domains/page/ui/DownloadMenuButton';

// Constants
const NO_DATA_TOGGLE_ROUTES = [
  '/valley-bank/assignment',
  '/valley-bank/advance-rate',
];

// Hooks
export const useCanToggleDataSource = () => {
  const location = useLocation();
  if (!location.pathname.startsWith('/valley-bank')) return false;
  return !NO_DATA_TOGGLE_ROUTES.some((path) =>
    location.pathname.startsWith(path),
  );
};

// Fragments
type ConfirmRestoreModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (confirmedRestoreDate: Util.FullDateStr) => void;
  restoreDate: Util.FullDateStr;
};
const ConfirmRestoreModal = ({
  isOpen,
  onCancel,
  onSubmit,
  restoreDate,
}: ConfirmRestoreModalProps) => {
  const cancelRef = React.useRef(null);
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Confirm Restore from Archive
          </AlertDialogHeader>

          <AlertDialogBody>
            <>
              <Text
                mb={2}
              >{`Reporting date ${restoreDate} is greater than ${VALLEY_BANK_ARCHIVE_THRESHOLD_DAYS} days old and needs to be restored from the archive.  This will take a few minutes to load. Confirm?`}</Text>
              <Box py="12px" alignSelf="flex-end">
                <HStack spacing="25px">
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme={buttonColorScheme}
                    onClick={() => onCancel()}
                    px="20px"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme={buttonColorScheme}
                    onClick={() => {
                      onSubmit(restoreDate);
                    }}
                    px="20px"
                  >
                    Submit
                  </Button>
                </HStack>
              </Box>
            </>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

const HeaderMessage = ({
  headerMessage,
  headerIcon,
  headerIconColor,
}: HeaderProps) => {
  if (!headerMessage || !headerIcon || !headerIconColor) return null;
  return (
    <HStack color={headerIconColor}>
      <Icon icon={headerIcon} />
      <Text>{headerMessage}</Text>
    </HStack>
  );
};

const AsOfDateDisplay = ({
  asOfDate,
}: {
  asOfDate: Util.FullDateStr | null;
}) => {
  return asOfDate ? (
    <HStack color={colors.black[60]}>
      <Icon icon="clock" />
      <Text>{`Data Sourced: ${commonDate.fullDateStrToHumanDateString(
        asOfDate,
      )}`}</Text>
    </HStack>
  ) : (
    // asOfDate will be lazy loaded on first data fetch
    <ProgressAlert status="info" bgColor="transparent">
      Loading latest data timestamp
    </ProgressAlert>
  );
};

// picks up date for historical mode
type DatePickerProps = {
  maxReportingDate: Util.FullDateStr;
  historicalReportingDate: Util.FullDateStr;
  setHistoricalReportingDate: (date: Util.FullDateStr) => void;
};
const DatePicker = ({
  maxReportingDate,
  historicalReportingDate,
  setHistoricalReportingDate,
}: DatePickerProps) => {
  const toast = useToast();

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState<boolean>(false);
  const [restoreDate, setRestoreDate] = useState<Util.FullDateStr | null>(null);

  const maxDate = commonDate.fullDateStrToDate(maxReportingDate);

  const selectedDate = commonDate.fullDateStrToDate(historicalReportingDate);

  const [
    restoreValleyBankReportingDate,
  ] = useRestoreValleyBankReportingDateMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */

    fetchPolicy: 'network-only',
    onCompleted() {
      toast({
        status: 'success',
        duration: 3000,
        title: 'Initiated restore.',
        position: 'top',
      });
    },
    onError() {
      toast({
        status: 'error',
        position: 'top',
        isClosable: true,
        title:
          'Unable to initiate restore. Please contact Finley for assistance.',
      });
    },
  });

  const setSelectedDate = (date: Date | null) => {
    if (!date) return;
    const currentSelection = commonDate.dateToFullDateString(date);

    const archiveThresold = getValleyBankArchiveThresoldDate();

    if (currentSelection < archiveThresold) {
      setRestoreDate(currentSelection);
      setIsRestoreModalOpen(true);
    } else {
      setRestoreDate(null);
      setIsRestoreModalOpen(false);
      setHistoricalReportingDate(currentSelection);
    }
  };

  const onSubmit = async (confirmedRestoreDate: Util.FullDateStr) => {
    await restoreValleyBankReportingDate({
      variables: { reportingDate: confirmedRestoreDate },
    });
    setRestoreDate(null);
    setIsRestoreModalOpen(false);
    setHistoricalReportingDate(confirmedRestoreDate);
  };

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <Flex gap="0.5rem" alignItems="center">
      {restoreDate && (
        <ConfirmRestoreModal
          isOpen={isRestoreModalOpen}
          onCancel={() => {
            setIsRestoreModalOpen(false);
          }}
          restoreDate={restoreDate}
          onSubmit={onSubmit}
        />
      )}
      <SimpleDatePicker
        width="170px"
        tz={clientTimeZone}
        dateFormat="MMMM dd, yyyy"
        minDate={new Date('2024-04-15')} // data prior does not exist or is not stable for restore (breaking changes), approximately (don't need to worry about browser date)
        maxDate={maxDate}
        selectedDate={selectedDate}
        showTimeSelect={false}
        setSelectedDate={setSelectedDate}
      />
      <Icon icon="calendar" />
    </Flex>
  );
};
type HeaderMessageForDashboardModeProps = {
  maxReportingDate: Util.FullDateStr;
  currentReportingDate: Util.FullDateStr;
  setReportingDate: (date: Util.FullDateStr) => void;
  dashboardMode: DashboardMode;
  asOfDate: Util.FullDateStr | null;
};
const HeaderMessageForDashboardMode = ({
  dashboardMode,
  asOfDate,
  currentReportingDate,
  maxReportingDate,
  setReportingDate,
}: HeaderMessageForDashboardModeProps) => {
  const headerElement = getHeaderElementProps(dashboardMode);

  switch (dashboardMode) {
    case 'current':
      return <AsOfDateDisplay asOfDate={asOfDate} />;
    case 'historical':
      return (
        <>
          <DatePicker
            maxReportingDate={maxReportingDate}
            setHistoricalReportingDate={setReportingDate}
            historicalReportingDate={currentReportingDate}
          />
          <AsOfDateDisplay asOfDate={asOfDate} />
        </>
      );

    case 'preview':
      return <HeaderMessage {...headerElement} />;
    default:
      return finleyAssertUnreachable(dashboardMode);
  }
};

// Exports
export const ValleyBankDataMedataHeader = () => {
  const {
    asOfDate,
    dashboardMode,
    isPreview,
    reportingDate,
    setDashboardMode,
    setReportingDate,
    currentReportingDate,
  } = useDataMetadataContext();
  const canToggleDataSource = useCanToggleDataSource();
  const container = useSubheaderDomNode();
  if (!canToggleDataSource || !container) return null;
  return createPortal(
    <HeaderRow
      FlexProps={{
        backgroundColor: getBgColorForDashboardMode(dashboardMode),
        position: 'relative',
      }}
    >
      <ValleyBankPreviewPicker
        dashboardMode={dashboardMode}
        setDashboardMode={setDashboardMode}
      />
      <HeaderMessageForDashboardMode
        dashboardMode={dashboardMode}
        asOfDate={asOfDate}
        setReportingDate={setReportingDate}
        currentReportingDate={reportingDate}
        maxReportingDate={commonDate.addDays(currentReportingDate, -1)}
      />
      <Spacer />
      <DownloadMenuButton reportingDate={reportingDate} isPreview={isPreview} />
    </HeaderRow>,
    container,
  );
};
